import React, { useState, useEffect } from 'react';
import MaterialTable from 'material-table';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Box,
  Chip,
} from '@material-ui/core';
import Swal from 'sweetalert2';
import request from '../../../services/Request';

const BASE = '/external/admin';

const ALL_EVENTS = [
  { value: 'member.registered', label: 'Member Registered' },
  { value: 'member.purchase', label: 'Member Purchase' },
  { value: 'order.completed', label: 'Order Completed' },
];

export default function Webhooks() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);

  const [url, setUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [events, setEvents] = useState(['member.registered', 'member.purchase', 'order.completed']);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    request({ method: 'get', url: `${BASE}/webhooks` }).then((res) => {
      if (!cancelled) {
        setWebhooks(res.data);
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [refresh]);

  const toggleEvent = (value) => {
    setEvents((prev) =>
      prev.includes(value) ? prev.filter((e) => e !== value) : [...prev, value]
    );
  };

  const handleCreate = async () => {
    if (!url.trim() || events.length === 0) return;
    try {
      await request({ method: 'post', url: `${BASE}/webhooks`, data: { url, secret, events } });
      Swal.fire({ icon: 'success', text: 'Webhook registered', timer: 1500, showConfirmButton: false });
      setOpen(false);
      setUrl('');
      setSecret('');
      setEvents(['member.registered', 'member.purchase', 'order.completed']);
      refresh ? setRefresh(false) : setRefresh(true);
    } catch (e) {
      Swal.fire({ icon: 'error', text: 'Failed to register webhook' });
    }
  };

  const handleDelete = async (id, webhookUrl) => {
    const confirm = await Swal.fire({
      title: 'Delete webhook?',
      text: webhookUrl,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      confirmButtonColor: '#d33',
    });
    if (!confirm.isConfirmed) return;
    await request({ method: 'delete', url: `${BASE}/webhooks/${id}` });
    Swal.fire({ icon: 'success', text: 'Webhook deleted', timer: 1200, showConfirmButton: false });
    refresh ? setRefresh(false) : setRefresh(true);
  };

  const columns = [
    { title: 'URL', field: 'url', render: (row) => (
      <Typography variant="body2" style={{ fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all' }}>
        {row.url}
      </Typography>
    )},
    { title: 'Events', field: 'events', render: (row) => (
      <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
        {(row.events || []).map((e) => (
          <Chip key={e} label={e} size="small" style={{ fontSize: 11 }} />
        ))}
      </Box>
    )},
    { title: 'Status', field: 'active', render: (row) => (
      <Chip
        label={row.active ? 'Active' : 'Inactive'}
        size="small"
        style={{ backgroundColor: row.active ? '#e8f5e9' : '#ffebee', color: row.active ? '#2e7d32' : '#c62828' }}
      />
    )},
    { title: 'Created', field: 'createdAt', render: (row) => new Date(row.createdAt).toLocaleDateString() },
  ];

  return (
    <div>
      <MaterialTable
        isLoading={loading}
        columns={columns}
        data={webhooks}
        title="Webhooks"
        actions={[
          {
            icon: 'add',
            tooltip: 'Register Webhook',
            isFreeAction: true,
            onClick: () => setOpen(true),
          },
          {
            icon: 'delete',
            tooltip: 'Delete Webhook',
            onClick: (e, row) => handleDelete(row._id, row.url),
          },
        ]}
        options={{ actionsColumnIndex: -1, pageSize: 10, search: false }}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Register Webhook</DialogTitle>
        <DialogContent>
          <TextField
            label="URL"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            fullWidth
            variant="outlined"
            placeholder="https://your-server.com/webhook"
            style={{ marginTop: 8, marginBottom: 16 }}
          />
          <TextField
            label="Secret (optional)"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            fullWidth
            variant="outlined"
            placeholder="Used to verify webhook signature"
            style={{ marginBottom: 16 }}
          />
          <Typography variant="body2" gutterBottom style={{ fontWeight: 600 }}>
            Events
          </Typography>
          <FormGroup>
            {ALL_EVENTS.map((ev) => (
              <FormControlLabel
                key={ev.value}
                control={
                  <Checkbox
                    checked={events.includes(ev.value)}
                    onChange={() => toggleEvent(ev.value)}
                    color="primary"
                  />
                }
                label={ev.label}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            color="primary"
            variant="contained"
            disabled={!url.trim() || events.length === 0}
          >
            Register
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
